import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import CosmoHeads from './CosmoHeads';
import ResultsCard from './ResultsCard';
import CosmoAttentionLevels from './CosmoAttentionLevels';

const AssessmentDashboard = () => {
  const { t } = useLanguage();
  const [selectedPatient, setSelectedPatient] = useState('patient1');

  // Dati di esempio per i pazienti 
  const patients = {
    patient1: {
      name: "John Doe",
      assessments: [
        {
          id: 1,
          title: "PID-5-BF+",
          score: 75,
          maxScore: 100,
          attentionLevel: "attention",
          date: "2024-05-10",
          description: "Moderate elevation in negative affectivity and disinhibition domains. Il corso PID-5-BF+ dura 52 minuti e offre una panoramica completa delle valutazioni di personalità secondo il modello DSM-5 AMPD. Questo test fornisce informazioni dettagliate sui tratti di personalità del paziente e può essere utilizzato per guidare decisioni cliniche."
        },
        {
          id: 2,
          title: "Compass of Shame",
          score: 32,
          maxScore: 60,
          attentionLevel: "normal",
          date: "2024-05-08",
          description: "Within normal range for shame responses. Questo assessment mostra che il paziente ha reazioni di vergogna nella norma rispetto alla popolazione generale. Non si osservano particolari problematiche relative ai meccanismi di coping legati alla vergogna."
        },
        {
          id: 3,
          title: "LPF Scale 2.0",
          score: 28,
          maxScore: 30,
          attentionLevel: "highAttention",
          date: "2024-05-12",
          description: "Significant impairment in identity integration and self-direction. I punteggi elevati indicano serie difficoltà nel funzionamento della personalità, particolarmente nelle aree di integrazione dell'identità e autodirezione. Si consiglia un'attenzione clinica immediata e un piano di trattamento mirato per affrontare queste problematiche."
        }
      ]
    },
    patient2: {
      name: "Jane Smith",
      assessments: [
        {
          id: 1,
          title: "PID-5-BF+",
          score: 42,
          maxScore: 100,
          attentionLevel: "normal",
          date: "2024-05-11",
          description: "All personality domains within normal limits. Non si osservano elevazioni significative nei domini di personalità valutati. Il profilo complessivo del paziente è nella norma rispetto alla popolazione generale."
        },
        {
          id: 2,
          title: "Compass of Shame",
          score: 48,
          maxScore: 60,
          attentionLevel: "attention",
          date: "2024-05-09",
          description: "Elevated scores on attack-self and withdrawal scales. Si osservano punteggi elevati nelle scale di attacco verso sé e ritiro, suggerendo potenziali difficoltà nella gestione della vergogna. Si consiglia un'attenzione clinica a questi aspetti durante il percorso terapeutico."
        }
      ]
    }
  };

  // Determina il livello di attenzione generale per il paziente
  const getOverallAttentionLevel = (assessments) => {
    if (assessments.some(a => a.attentionLevel === 'highAttention')) {
      return 'highAttention';
    } else if (assessments.some(a => a.attentionLevel === 'attention')) {
      return 'attention';
    }
    return 'normal';
  };

  const currentPatient = patients[selectedPatient];
  const overallAttention = getOverallAttentionLevel(currentPatient.assessments);

  return (
    <div className="bg-gray-50 p-6 rounded-xl">
      <div className="max-w-4xl mx-auto">
        {/* Spiegazione delle teste di COSMO */}
        <CosmoAttentionLevels />

        {/* Selezione paziente */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Seleziona Paziente:</label>
          <div className="relative">
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 pl-3 pr-10 py-2 text-base bg-white"
            >
              {Object.keys(patients).map((key) => (
                <option key={key} value={key}>{patients[key].name}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Dashboard del paziente */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{currentPatient.name}</h2>
              <p className="text-gray-600">Patient Dashboard</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-gray-700">Overall Status:</span>
              <CosmoHeads attentionLevel={overallAttention} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentPatient.assessments.map((assessment) => (
              <ResultsCard
                key={assessment.id}
                title={assessment.title}
                score={assessment.score}
                maxScore={assessment.maxScore}
                attentionLevel={assessment.attentionLevel}
                description={assessment.description}
                date={assessment.date}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentDashboard;